import { z } from 'zod';
import {
  countdownSchema,
  isoDateSchema,
  moneyAmountSchema,
  priceSchema,
  renewalConditionSchema,
  serviceTypeSchema,
  uuidSchema,
} from '../common';
import { pageQuerySchema, pageSchema } from './pagination';

/**
 * Renewal contracts (DECISIONS.md A-R1–A-R12). Viewing needs `renewals.view` (Admin and
 * Operations); opening, editing and renewing need `renewals.manage` (Admin). The admin enters all
 * dates; currency is fixed to USD by the server. Bodies are strict.
 */
export const renewalOpenSchema = z.strictObject({
  termStart: isoDateSchema,
  renewalDate: isoDateSchema,
  price: moneyAmountSchema,
});
export type RenewalOpen = z.infer<typeof renewalOpenSchema>;

export const renewalUpdateSchema = z
  .strictObject({
    termStart: isoDateSchema.optional(),
    renewalDate: isoDateSchema.optional(),
    price: moneyAmountSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'no fields to update' });
export type RenewalUpdate = z.infer<typeof renewalUpdateSchema>;

/**
 * Renew (A-R11): termStart defaults to the closing term's renewal date; price is always
 * required (never carried forward); renewalDate must be later than the closing renewal date.
 */
export const renewalRenewSchema = z.strictObject({
  termStart: isoDateSchema.optional(),
  renewalDate: isoDateSchema,
  price: moneyAmountSchema,
});
export type RenewalRenew = z.infer<typeof renewalRenewSchema>;

export const renewalParamsSchema = z.object({ renewalId: uuidSchema });

/**
 * A term renews a service, or an infrastructure resource (the VPS, the domain; director,
 * 2026-11-02): exactly one of `serviceId` / `resourceId` is set. `subjectName` is what it renews in
 * words — the service's or the entry's name, nothing more technical.
 */
export const renewalSchema = z.object({
  id: uuidSchema,
  serviceId: uuidSchema.nullable(),
  serviceType: serviceTypeSchema.nullable(),
  resourceId: uuidSchema.nullable(),
  subjectName: z.string(),
  state: z.enum(['open', 'closed']),
  termStart: z.string(),
  renewalDate: z.string(),
  price: priceSchema,
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Derived for open terms only; null for closed terms. */
  condition: renewalConditionSchema.nullable(),
  countdown: countdownSchema.nullable(),
});
export type Renewal = z.infer<typeof renewalSchema>;

export const renewalListSchema = z.object({ items: z.array(renewalSchema) });

export const renewResultSchema = z.object({
  closed: renewalSchema,
  opened: renewalSchema,
});

export const upcomingRenewalsQuerySchema = pageQuerySchema.extend({
  /** Only open terms renewing on or before this date. */
  before: isoDateSchema.optional(),
});

export const upcomingRenewalPageSchema = pageSchema(renewalSchema);

export const renewalHistoryEntrySchema = z.object({
  id: z.number().int(),
  renewalId: uuidSchema,
  event: z.enum(['term_opened', 'term_updated', 'term_closed']),
  changes: z.record(z.string(), z.unknown()),
  actorType: z.enum(['admin', 'operations', 'system', 'integration']),
  actorId: uuidSchema.nullable(),
  occurredAt: z.string(),
});
export type RenewalHistoryEntry = z.infer<typeof renewalHistoryEntrySchema>;

export const renewalHistoryListSchema = z.object({ items: z.array(renewalHistoryEntrySchema) });
